import {use, useCallback} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ContentPreviewContext} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {File} from '@github-ui/copilot-immersive-v1/components/ContentPreview/content-preview-types'

import type {Artifact} from '../../utils/artifacts/artifact-core'

/**
 * Sentinel the workbench uses for previewable content that no chat message
 * produced. Duplicated from `@github-ui/copilot-chat`'s `NullMessageId` rather
 * than depending on that package for a single string.
 */
const NULL_MESSAGE_ID = 'NULL_MESSAGE'

/**
 * Open an artifact in the shared content-preview workbench.
 *
 * Returns `undefined` when no workbench is mounted above this component, which
 * is the same graceful degradation `ClickableGitHubReferenceCards` uses for
 * GitHub reference cards: the artifacts list still renders, its rows just are
 * not clickable. Reading the context directly — rather than through
 * `useContentPreview`, which throws without a provider — is what keeps this
 * usable on the standalone agent-session routes, and means the workbench needs
 * no changes to support artifacts.
 *
 * Only whole-file writes carry their contents, so an artifact without `content`
 * has nothing to show and is deliberately not openable; a partial edit rendered
 * as a whole file would misrepresent what is on disk.
 */
export function useOpenArtifactPreview(): ((artifact: Artifact, event: React.MouseEvent) => void) | undefined {
  const contentPreview = use(ContentPreviewContext)

  const updateItem = contentPreview?.updateItem
  const openItem = contentPreview?.openItem
  const openPreviewPane = contentPreview?.openPreviewPane

  const open = useCallback(
    (artifact: Artifact, event: React.MouseEvent) => {
      if (!updateItem || !openItem || !openPreviewPane) return
      const value = artifact.content
      if (value === undefined) return

      const item: File = {
        type: 'file',
        // Derived from the path so reopening the same artifact reuses its tab.
        id: `file:artifact:${artifact.absolutePath}#0`,
        // The file came from a tool call, not a chat message, so there is no
        // message to anchor it to. Matches how the workbench is driven from
        // reference clicks elsewhere.
        messageId: NULL_MESSAGE_ID,
        name: artifact.displayPath,
        language: artifact.language,
        value,
        isStreaming: false,
        isUserEdited: false,
        timestamp: new Date(),
      }

      updateItem(item)
      openItem(item.id)
      openPreviewPane(event.currentTarget as HTMLElement)
    },
    [updateItem, openItem, openPreviewPane],
  )

  return contentPreview ? open : undefined
}
